import type { FeatureFlagInfo } from '../constants/featureFlagCatalog';
import type { NexoyaTeamFeatureFlag } from '../types';

type FeatureFlagLike = Pick<NexoyaTeamFeatureFlag, 'name' | 'status'> | null | undefined;

export const isFeatureFlagEnabled = (
  featureFlags: ReadonlyArray<FeatureFlagLike> | null | undefined,
  flagName: string,
): boolean => (featureFlags ?? []).some((ff) => ff?.name === flagName && ff?.status === true);

// `info` is missing for a flag the backend added before the catalog described it.
export type FeatureFlagRow = { name: string; status: boolean; info?: FeatureFlagInfo };

export type FeatureFlagSort = 'name' | 'newest' | 'oldest' | 'enabled';

const labelOf = (row: FeatureFlagRow) => row.info?.label ?? row.name;

const byLabel = (a: FeatureFlagRow, b: FeatureFlagRow) =>
  labelOf(a).localeCompare(labelOf(b), 'en', { sensitivity: 'base' });

// Undated flags sink to the bottom in both date orders.
const byDate = (direction: 1 | -1) => (a: FeatureFlagRow, b: FeatureFlagRow) => {
  const left = a.info?.liveSince;
  const right = b.info?.liveSince;
  if (left === right) return byLabel(a, b);
  if (!left) return 1;
  if (!right) return -1;
  return direction * left.localeCompare(right);
};

const byEnabled = (a: FeatureFlagRow, b: FeatureFlagRow) => Number(b.status) - Number(a.status) || byLabel(a, b);

const COMPARATORS: Record<FeatureFlagSort, (a: FeatureFlagRow, b: FeatureFlagRow) => number> = {
  name: byLabel,
  newest: byDate(-1),
  oldest: byDate(1),
  enabled: byEnabled,
};

export const sortFeatureFlags = (rows: ReadonlyArray<FeatureFlagRow>, sort: FeatureFlagSort): FeatureFlagRow[] =>
  [...rows].sort(COMPARATORS[sort]);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatLiveSince = (liveSince: string): string => {
  const [year, month] = liveSince.split('-');
  return `${MONTHS[Number(month) - 1]} ${year}`;
};

// Drives the "New" badge: the month a flag went live and the two after it.
export const isRecentlyLive = (liveSince: string, today: Date): boolean => {
  const [year, month] = liveSince.split('-').map(Number);
  const monthsAgo = (today.getFullYear() - year) * 12 + today.getMonth() + 1 - month;
  return monthsAgo >= 0 && monthsAgo <= 2;
};
